import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Sparkles, Menu, Gift } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const NAV = [
  { label: "New", to: "/collections/new" },
  { label: "Tees", to: "/collections/tees" },
  { label: "Sets", to: "/collections/sets" },
  { label: "Shorts", to: "/collections/shorts" },
  { label: "Headwear", to: "/collections/headwear" },
  { label: "Shop All", to: "/collections/all" },
];

export const Header = () => {
  const { cartCount, wishlist, setCartOpen, setStylistOpen } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 transition-all duration-300 bg-[#EDEDED]/90 backdrop-blur-xl border-b border-black/10"
    >
      <div className="px-5 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <div className="flex items-center gap-6">
            <Sheet>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-trigger" className="lg:hidden" aria-label="Menu"><Menu strokeWidth={1.25} /></button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#0A0A0A] text-[#F4F2ED] border-none w-[82%]">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <div className="font-hero text-3xl mb-10 pt-4">LÉVINCHI</div>
                <nav className="flex flex-col gap-6">
                  {NAV.map((n) => (
                    <Link key={n.to} to={n.to} data-testid={`mnav-${n.label.toLowerCase()}`} className="font-display text-2xl uppercase tracking-tight">{n.label}</Link>
                  ))}
                  <Link to="/rewards" data-testid="mnav-rewards" className="font-display text-2xl uppercase tracking-tight text-[#F4F2ED]">Rewards</Link>
                </nav>
              </SheetContent>
            </Sheet>

            <nav className="hidden lg:flex items-center gap-8">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`} className="overline link-underline">{n.label}</Link>
              ))}
            </nav>
          </div>

          <Link to="/" data-testid="brand-logo" className="font-hero text-xl sm:text-2xl lg:text-3xl absolute left-1/2 -translate-x-1/2 tracking-tight">
            LÉVINCHI&nbsp;LUXE
          </Link>

          <div className="flex items-center gap-4 sm:gap-5">
            <button data-testid="open-stylist" onClick={() => setStylistOpen(true)} className="hidden sm:flex items-center gap-2 overline group" aria-label="AI Stylist">
              <Sparkles size={17} strokeWidth={1.25} className="text-[#0A0A0A]" />
              <span className="hidden lg:inline link-underline">Stylist</span>
            </button>
            <Link to="/rewards" data-testid="nav-rewards" className="hidden sm:block" aria-label="Rewards"><Gift size={20} strokeWidth={1.25} /></Link>
            <button onClick={() => navigate("/wishlist")} data-testid="open-wishlist" className="relative" aria-label="Wishlist">
              <Heart size={20} strokeWidth={1.25} />
              {wishlist.length > 0 && <span className="absolute -top-2 -right-2 bg-[#0A0A0A] text-[#F4F2ED] text-[10px] w-4 h-4 flex items-center justify-center font-mono">{wishlist.length}</span>}
            </button>
            <button onClick={() => setCartOpen(true)} data-testid="open-cart" className="relative" aria-label="Cart">
              <ShoppingBag size={20} strokeWidth={1.25} />
              {cartCount > 0 && <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-[#0A0A0A] text-[#F4F2ED] text-[10px] w-4 h-4 flex items-center justify-center font-mono font-bold">{cartCount}</span>}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};
